import { z } from '@repo/schema'

import { getImpersonatorId, getTokenExpiration, getTokenSubject, isTokenExpired } from './token'

const SESSION_STORAGE_KEY = 'ck-impersonation-recovery-v1'

const RecoveryUserId = z.union([z.number(), z.string()]).transform(value => String(value))

const RecoverySchema = z
  .object({
    adminToken: z.string().min(1),
    adminUserId: RecoveryUserId,
    impersonationTokenExpiresAt: z.number().positive(),
    originalTokenExpiresAt: z.number().positive(),
    targetUserEmail: z.string(),
    targetUserId: RecoveryUserId,
    targetUserName: z.string(),
    version: z.literal(1),
  })
  .strict()

export type ImpersonationRecovery = z.infer<typeof RecoverySchema>

interface SaveRecoveryInput {
  adminToken: string
  adminUserId: number | string
  impersonationToken: string
  targetUser: {
    email: string
    id: number | string
    name: string
  }
}

const parseUserId = (value: null | number | string | undefined): null | string => {
  if (value === null || value === undefined) return null
  const text = String(value)
  return text.length > 0 ? text : null
}

const getStoredRecovery = (): ImpersonationRecovery | null => {
  if (typeof window === 'undefined') return null

  const raw = sessionStorage.getItem(SESSION_STORAGE_KEY)
  if (!raw) return null

  try {
    const parsed = RecoverySchema.safeParse(JSON.parse(raw))
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

export const saveImpersonationRecovery = (input: SaveRecoveryInput): boolean => {
  if (typeof window === 'undefined') return false

  const now = Date.now()

  const adminUserId = parseUserId(input.adminUserId)
  const targetUserId = parseUserId(input.targetUser.id)

  if (!adminUserId || !targetUserId) {
    return false
  }

  const adminTokenExpiresAt = getTokenExpiration(input.adminToken)
  const impersonationTokenExpiresAt = getTokenExpiration(input.impersonationToken)

  if (adminTokenExpiresAt === null || impersonationTokenExpiresAt === null) {
    return false
  }

  const impersonationSubject = getTokenSubject(input.impersonationToken)
  if (impersonationSubject !== targetUserId) {
    return false
  }

  const impersonatorId = getImpersonatorId(input.impersonationToken)
  if (impersonatorId === null || String(impersonatorId) !== adminUserId) {
    return false
  }

  const adminExpMs = adminTokenExpiresAt * 1000
  const impersonationExpMs = impersonationTokenExpiresAt * 1000

  if (getTokenSubject(input.adminToken) !== adminUserId) {
    return false
  }

  if (adminExpMs <= now || impersonationExpMs <= now) {
    return false
  }

  const payload: ImpersonationRecovery = {
    adminToken: input.adminToken,
    adminUserId,
    impersonationTokenExpiresAt: impersonationExpMs,
    originalTokenExpiresAt: adminExpMs,
    targetUserEmail: input.targetUser.email,
    targetUserId,
    targetUserName: input.targetUser.name,
    version: 1,
  }

  const normalized = RecoverySchema.safeParse(payload)
  if (!normalized.success) {
    return false
  }

  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(normalized.data))
    return true
  } catch {
    return false
  }
}

export const getImpersonationRecovery = (): ImpersonationRecovery | null => {
  return getStoredRecovery()
}

export const clearImpersonationRecovery = () => {
  if (typeof window === 'undefined') return

  sessionStorage.removeItem(SESSION_STORAGE_KEY)
}

export const getImpersonationRecoveryRemainingMs = (
  recovery: ImpersonationRecovery,
  now = Date.now(),
): number => {
  return Math.max(0, recovery.impersonationTokenExpiresAt - now)
}

export const getImpersonationRecoveryRemainingSeconds = (
  recovery: ImpersonationRecovery,
  now = Date.now(),
): number => {
  return Math.floor(getImpersonationRecoveryRemainingMs(recovery, now) / 1000)
}

export const isImpersonationRecoveryExpired = (
  recovery: ImpersonationRecovery,
  now = Date.now(),
): boolean => {
  return recovery.impersonationTokenExpiresAt <= now || recovery.originalTokenExpiresAt <= now
}

export const isImpersonationRecoveryAdminTokenValid = (
  recovery: ImpersonationRecovery,
  now = Date.now(),
): boolean => {
  if (getTokenSubject(recovery.adminToken) !== recovery.adminUserId) return false

  return recovery.originalTokenExpiresAt > now && !isTokenExpired(recovery.adminToken, now)
}

export const isImpersonationRecoveryBindingValid = (
  recovery: ImpersonationRecovery,
  impersonationToken: string,
): boolean => {
  const impersonationSubject = getTokenSubject(impersonationToken)
  if (impersonationSubject !== recovery.targetUserId) return false

  const impersonatorId = getImpersonatorId(impersonationToken)
  return impersonatorId !== null && String(impersonatorId) === recovery.adminUserId
}

export const getAdminToken = (recovery: ImpersonationRecovery): string => recovery.adminToken

export const getImpersonationRecoveryTargetLabel = (recovery: ImpersonationRecovery): string => {
  return `${recovery.targetUserName} (${recovery.targetUserEmail})`
}
